import { jsPDF } from "jspdf";
import { GROUP } from "./CompanyInfo";

/**
 * Landscape PDF export for the report tables.
 *
 * The reports could only be downloaded as a spreadsheet, which is the wrong
 * shape for the common case - printing a ledger or a pending-bills list, or
 * mailing it to someone who just wants to read it. This renders the same rows
 * the screen shows onto A4 landscape (the tables are wide; portrait either
 * crushed the columns or cut them off).
 *
 * Drawn with jsPDF's primitives rather than jspdf-autotable so the app does
 * not gain a dependency: jspdf is already installed.
 *
 * @param {object}   options
 * @param {string}   options.title      report name, e.g. "View Ledger"
 * @param {string[]} options.subtitle   context lines - firm, date range
 * @param {object[]} options.columns    [{ key, label, align, wrap, weight }]
 * @param {object[]} options.rows       the data rows, as shown on screen
 * @param {string}   options.fileName   without the .pdf
 */
export function exportReportPdf({
    title,
    subtitle = [],
    columns = [],
    rows = [],
    fileName = "report",
}) {
    if (!columns.length) {
        return;
    }

    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });

    const PAGE_W = doc.internal.pageSize.getWidth();
    const PAGE_H = doc.internal.pageSize.getHeight();
    const MARGIN = 26;
    const PAD = 5;              /* inside a cell */
    const FONT = 8.5;
    const LINE = 10.5;

    const PURPLE = [60, 43, 153];
    const GREEN = [0, 167, 89];
    const INK = [35, 35, 59];
    const MUTED = [110, 110, 130];
    const STRIPE = [246, 246, 251];
    const RULE = [223, 225, 236];

    const tableW = PAGE_W - MARGIN * 2;

    const text = (row, column) => {
        const value = column.format
            ? column.format(row[column.key], row)
            : row[column.key];

        return value === null || value === undefined ? "" : String(value);
    };

    /* ---- column widths ----
       Measured from the real strings (a sample is enough for a long report),
       then scaled to the page. A column that is allowed to wrap gives up its
       extra width first, so the numbers never get squeezed. */
    doc.setFontSize(FONT);

    const sample = rows.slice(0, 400);

    const natural = columns.map((column) => {
        /* the heading is allowed to wrap, so a column only has to be as wide
           as its longest WORD - "Previous Last Sale" over two lines leaves
           room for the product name instead of eating it */
        doc.setFont("helvetica", "bold");
        const headWidth = String(column.label || "")
            .split(/\s+/)
            .reduce((most, word) => Math.max(most, doc.getTextWidth(word)), 0);

        doc.setFont("helvetica", "normal");
        const cells = sample.map((row) => doc.getTextWidth(text(row, column)));

        let widest;

        if (column.wrap && cells.length > 4) {
            /* A wrapping column is sized for the rows it actually has, not for
               its one longest entry. Particulars is usually "Sale Invoice" or
               "Receipt" with the odd long product name among them; measuring
               the longest gave that column half the page and left a band of
               white space on every other row.

               The 85th percentile covers the great majority of rows as one
               line and lets the few long ones wrap onto two. */
            const sorted = cells.slice().sort((a, b) => a - b);
            const at = Math.floor((sorted.length - 1) * 0.85);

            widest = Math.max(sorted[at], headWidth);
        } else {
            widest = cells.reduce((most, width) => Math.max(most, width), headWidth);
        }

        /* +2pt so a column that measured exactly its widest string does not
           wrap that string onto two lines through rounding */
        return Math.max(widest + PAD * 2 + 2, 34);
    });

    const total = natural.reduce((sum, width) => sum + width, 0);

    if (total > tableW) {
        /* take the overflow out of the wrapping columns first */
        const wrappable = columns
            .map((column, i) => (column.wrap ? i : -1))
            .filter((i) => i >= 0);

        let overflow = total - tableW;

        wrappable.forEach((i) => {
            const spare = Math.max(natural[i] - 120, 0);
            const take = Math.min(spare, overflow);
            natural[i] -= take;
            overflow -= take;
        });

        if (overflow > 0) {
            /* still too wide - shrink everything proportionally */
            const scale = tableW / natural.reduce((sum, w) => sum + w, 0);
            natural.forEach((w, i) => {
                natural[i] = w * scale;
            });
        }
    } else if (total > 0) {
        /* Spare width is shared out in proportion, so every column gets the
           same bit of breathing room.

           It used to go entirely to the wrapping columns, which is why
           Particulars on the ledger came out enormous with a band of white
           space in it while the figures stayed cramped: a report whose rows
           are all short handed that column the whole surplus.

           A table whose columns already want most of the page is stretched to
           fill it, which reads as a normal report. A small one - the
           two-column invoice list - is not: stretching a bill number across
           an A4 landscape page looks worse than letting the table end where
           it ends, so its growth is capped. */
        const ratio = tableW / total;
        const scale = total / tableW >= 0.45 ? ratio : Math.min(ratio, 1.6);

        natural.forEach((width, i) => {
            natural[i] = width * scale;
        });
    }

    const widths = natural;

    /* what the table actually spans - at or under the printable width */
    const tableWidth = widths.reduce((sum, width) => sum + width, 0);
    const x0 = columns.map((column, i) =>
        MARGIN + widths.slice(0, i).reduce((sum, w) => sum + w, 0)
    );

    /* ---- the heading lines, and so the height of the header band ---- */
    doc.setFont("helvetica", "bold");

    const heads = columns.map((column, i) =>
        doc.splitTextToSize(String(column.label || ""), widths[i] - PAD * 2)
    );

    const headLines = heads.reduce((most, head) => Math.max(most, head.length), 1);
    const HEAD_H = headLines * LINE + 9;

    /* ---- the lines of every cell, and so the height of every row ---- */
    doc.setFont("helvetica", "normal");

    const body = rows.map((row) => {
        const cells = columns.map((column, i) =>
            doc.splitTextToSize(text(row, column), widths[i] - PAD * 2)
        );

        const lines = cells.reduce((most, cell) => Math.max(most, cell.length), 1);

        return { cells, height: lines * LINE + PAD * 2 };
    });

    /* ---- page furniture ---- */
    let y = 0;

    const drawTitle = () => {
        doc.setTextColor(...PURPLE);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.text(GROUP.name, MARGIN, MARGIN + 10);

        doc.setFontSize(11);
        doc.text(title, MARGIN, MARGIN + 26);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.5);
        doc.setTextColor(...MUTED);

        subtitle.filter(Boolean).forEach((line, i) => {
            doc.text(String(line), MARGIN, MARGIN + 40 + i * 11);
        });

        const stamp =
            "Generated " +
            new Date().toLocaleString("en-IN", { hour12: true }).replace(",", "");

        doc.text(stamp, PAGE_W - MARGIN, MARGIN + 10, { align: "right" });

        y = MARGIN + 40 + Math.max(subtitle.filter(Boolean).length, 1) * 11 + 6;
    };

    const drawHead = () => {
        doc.setFillColor(...PURPLE);
        doc.rect(MARGIN, y, tableWidth, HEAD_H, "F");

        doc.setFillColor(...GREEN);
        doc.rect(MARGIN, y + HEAD_H - 1.6, tableWidth, 1.6, "F");

        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(FONT);

        columns.forEach((column, i) => {
            const right = column.align === "right";
            const top =
                y + (HEAD_H - heads[i].length * LINE) / 2 + LINE - 2.5;

            doc.text(
                heads[i],
                right ? x0[i] + widths[i] - PAD : x0[i] + PAD,
                top,
                { align: right ? "right" : "left" }
            );
        });

        y += HEAD_H;
    };

    const newPage = (first) => {
        if (!first) {
            doc.addPage();
        }

        drawTitle();
        drawHead();
    };

    newPage(true);

    const bottom = PAGE_H - MARGIN - 14;

    body.forEach((row, index) => {
        if (y + row.height > bottom) {
            newPage(false);
        }

        if (index % 2 === 1) {
            doc.setFillColor(...STRIPE);
            doc.rect(MARGIN, y, tableWidth, row.height, "F");
        }

        doc.setTextColor(...INK);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(FONT);

        row.cells.forEach((cell, i) => {
            const column = columns[i];
            const right = column.align === "right";

            doc.text(
                cell,
                right ? x0[i] + widths[i] - PAD : x0[i] + PAD,
                y + PAD + LINE - 2.5,
                { align: right ? "right" : "left" }
            );
        });

        doc.setDrawColor(...RULE);
        doc.setLineWidth(0.4);
        doc.line(MARGIN, y + row.height, MARGIN + tableWidth, y + row.height);

        y += row.height;
    });

    /* ---- page numbers, once the total is known ---- */
    const pages = doc.getNumberOfPages();

    for (let page = 1; page <= pages; page += 1) {
        doc.setPage(page);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(...MUTED);
        doc.text(
            "Page " + page + " of " + pages,
            PAGE_W - MARGIN,
            PAGE_H - MARGIN + 2,
            { align: "right" }
        );
    }

    doc.save(fileName + ".pdf");
}

export default exportReportPdf;
